import { createFileRoute } from '@tanstack/react-router';
import { Stack, Tabs, Title } from '@mantine/core';
import { IconBrandFacebook, IconBrandInstagram, IconBrandWhatsapp } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { WhatsAppTemplatesPanel } from '../../components/templates/WhatsAppTemplatesPanel';
import { InstagramTemplatesPanel } from '../../components/templates/InstagramTemplatesPanel';
import { MessengerTemplatesPanel } from '../../components/templates/MessengerTemplatesPanel';
import { useTabParam } from '../../lib/useTabParam';

const CHANNELS = ['whatsapp', 'instagram', 'messenger'] as const;
type Channel = (typeof CHANNELS)[number];

/** Templates hub — one page, a tab per channel (was three separate sidebar
 *  entries). The active channel is URL-driven (`?channel=`) so the nav,
 *  old-route redirects, and refresh land on the right tab. Build/edit flows stay
 *  on their own per-channel routes. */
function TemplatesHub() {
  const [channel, setChannel] = useTabParam<Channel>('channel', CHANNELS, 'whatsapp');
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Title order={2}>Templates</Title>
        <Tabs
          value={channel}
          onChange={(v) => setChannel((v as Channel) ?? 'whatsapp')}
          keepMounted={false}
        >
          <Tabs.List mb="md">
            <Tabs.Tab value="whatsapp" leftSection={<IconBrandWhatsapp size={14} />}>
              WhatsApp
            </Tabs.Tab>
            <Tabs.Tab value="instagram" leftSection={<IconBrandInstagram size={14} />}>
              Instagram
            </Tabs.Tab>
            <Tabs.Tab value="messenger" leftSection={<IconBrandFacebook size={14} />}>
              Messenger
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="whatsapp">
            <WhatsAppTemplatesPanel />
          </Tabs.Panel>
          <Tabs.Panel value="instagram">
            <InstagramTemplatesPanel />
          </Tabs.Panel>
          <Tabs.Panel value="messenger">
            <MessengerTemplatesPanel />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/templates/')({
  component: TemplatesHub,
  validateSearch: (s: Record<string, unknown>): { channel?: Channel } => {
    const c = s.channel;
    return typeof c === 'string' && (CHANNELS as readonly string[]).includes(c)
      ? { channel: c as Channel }
      : {};
  },
});
